/**
 * JobTypeSelector Component
 *
 * Dropdown selector for choosing a job type.
 * Displays job type name and description.
 *
 * DDD Compliance:
 * - Purely presentational
 * - Uses real API via React Query hook
 * - Emits selection through onChange callback
 */

import { Select } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { useActiveJobTypes } from '@/application/service-provider/hooks/useJobTypes';

export interface JobTypeSelectorProps {
  value?: string;
  onChange: (typeId: string) => void;
  disabled?: boolean;
  required?: boolean;
}

export function JobTypeSelector({
  value,
  onChange,
  disabled,
  required,
}: JobTypeSelectorProps) {
  // Fetch active job types using React Query
  const { data: jobTypes = [], isLoading, isError } = useActiveJobTypes();

  const selectedType = jobTypes.find((jt) => jt.id === value);

  return (
    <div className="space-y-2">
      <Label htmlFor="job-type-selector">
        Job Type
        {required && <span className="text-red-500 ml-1">*</span>}
      </Label>
      <Select
        id="job-type-selector"
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled || isLoading}
      >
        <option value="">
          {isLoading
            ? 'Loading job types...'
            : isError
              ? 'Error loading job types'
              : 'Select a job type (optional)'}
        </option>
        {jobTypes.map((jobType) => (
          <option key={jobType.id} value={jobType.id}>
            {jobType.name}
          </option>
        ))}
      </Select>
      {selectedType && (
        <p className="text-xs text-neutral-600 mt-1">{selectedType.description}</p>
      )}
      <p className="text-xs text-neutral-500">
        Selecting a job type enables custom fields specific to that service
      </p>
    </div>
  );
}
